import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Skeleton,
  Paper,
  Stack
} from '@mui/material';
import { HourglassEmpty, Article } from '@mui/icons-material';

/**
 * Reusable Loading State Component
 * 
 * @param {string} variant - Loading style: 
 *   'linear' | 'linearStyled' | 'skeleton' | 'iconProgress' | 'pulse' | 
 *   'dots' | 'card' | 'waveBars' | 'gridSkeleton' | 'circular'
 * @param {string} message - Loading message to display
 * @param {string} subtitle - Optional subtitle text
 * @param {number} skeletonRows - Number of skeleton rows (default: 5)
 * @param {string} size - Size of the loader: 'small' | 'medium' | 'large' (default: 'medium')
 * @param {string} layout - Layout style for circular variant: 'inline' | 'stacked' (default: 'inline')
 * @param {object} sx - Additional MUI sx props
 */
const Loader = ({ 
  variant = 'circular',
  message = 'Loading...',
  subtitle = null,
  skeletonRows = 5,
  size = 'medium',
  layout = 'inline',
  sx = {}
}) => {
  
  // Size configurations
  const sizeConfig = {
    small: {
      circularSize: 20,
      iconSize: 60,
      iconInnerSize: 24,
      pulseSize: 50,
      dotsSize: 12,
      waveBarsHeight: 40,
      waveBarsWidth: 6,
      minHeight: 30,
      padding: 4,
      textVariant: 'body1'
    },
    medium: {
      circularSize: 60,
      iconSize: 80,
      iconInnerSize: 40,
      pulseSize: 80,
      dotsSize: 16,
      waveBarsHeight: 60,
      waveBarsWidth: 8,
      minHeight: 400,
      padding: 8,
      textVariant: 'h6'
    },
    large: {
      circularSize: 80,
      iconSize: 100,
      iconInnerSize: 50,
      pulseSize: 100,
      dotsSize: 20,
      waveBarsHeight: 80,
      waveBarsWidth: 10,
      minHeight: 500,
      padding: 10,
      textVariant: 'h5'
    }
  };
  
  const sizes = sizeConfig[size] || sizeConfig.medium;
  
  // Render based on variant
  switch (variant) {
    case 'linear':
      return (
        <Box sx={sx}>
          <LinearProgress />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: sizes.padding }}>
            <Typography variant={sizes.textVariant} color="text.secondary">
              {message}
            </Typography>
          </Box>
        </Box>
      );

    case 'linearStyled':
      return (
        <Box sx={{ width: '100%', py: sizes.padding, ...sx }}>
          <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
            <Typography variant={sizes.textVariant} gutterBottom color="text.secondary">
              {message}
            </Typography>
            <LinearProgress 
              sx={{ 
                mt: 2,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
                }
              }} 
            />
          </Box>
        </Box>
      );

    case 'skeleton':
      return (
        <Box sx={{ py: 2, ...sx }}>
          <Typography variant={sizes.textVariant} gutterBottom color="text.secondary">
            {message}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {[...Array(skeletonRows)].map((_, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="40%" height={20} />
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      );

    case 'iconProgress':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: sizes.padding, ...sx }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={sizes.iconSize} thickness={4} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HourglassEmpty sx={{ fontSize: sizes.iconInnerSize, color: 'primary.contrastText' }} />
            </Box>
          </Box>
          <Typography variant={sizes.textVariant} sx={{ mt: 3 }} color="text.secondary">
            {message}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.disabled">
              {subtitle}
            </Typography>
          )}
        </Box>
      );

    case 'pulse':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: sizes.padding, ...sx }}>
          <Box
            sx={{
              width: sizes.pulseSize,
              height: sizes.pulseSize,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 0.6,
                  transform: 'scale(0.95)',
                },
                '50%': {
                  opacity: 1,
                  transform: 'scale(1.05)',
                },
              },
            }}
          />
          <Typography variant={sizes.textVariant} sx={{ mt: 3 }} color="text.secondary">
            {message}
          </Typography>
        </Box>
      );

    case 'dots':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: sizes.padding, ...sx }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: sizes.dotsSize,
                  height: sizes.dotsSize,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  animation: 'bounce 1.4s ease-in-out infinite',
                  animationDelay: `${index * 0.2}s`,
                  '@keyframes bounce': {
                    '0%, 100%': {
                      transform: 'translateY(0)',
                      opacity: 0.5,
                    },
                    '50%': {
                      transform: 'translateY(-20px)',
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
          <Typography variant={sizes.textVariant} sx={{ mt: 3 }} color="text.secondary">
            {message}
          </Typography>
        </Box>
      );

    case 'card':
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: sizes.padding, ...sx }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              maxWidth: 400, 
              width: '100%',
              textAlign: 'center',
              borderRadius: 2
            }}
          >
            <Article sx={{ fontSize: 60, color: 'primary.contrastText', mb: 2 }} />
            <Typography variant={sizes.textVariant} gutterBottom>
              {message}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {subtitle}
              </Typography>
            )}
            <LinearProgress 
              sx={{ 
                height: 6,
                borderRadius: 3,
              }} 
            />
          </Paper>
        </Box>
      );

    case 'waveBars':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: sizes.padding, ...sx }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: sizes.waveBarsHeight, mb: 3 }}>
            {[0, 1, 2, 3, 4].map((index) => (
              <Box
                key={index}
                sx={{
                  width: sizes.waveBarsWidth,
                  height: sizes.waveBarsHeight,
                  backgroundColor: 'primary.contrastText',
                  borderRadius: 1,
                  animation: 'wave 1.2s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`,
                  '@keyframes wave': {
                    '0%, 100%': {
                      transform: 'scaleY(0.3)',
                    },
                    '50%': {
                      transform: 'scaleY(1)',
                    },
                  },
                }}
              />
            ))}
          </Box>
          <Typography variant={sizes.textVariant} color="text.secondary">
            {message}
          </Typography>
        </Box>
      );

    case 'gridSkeleton':
      return (
        <Box sx={{ py: 2, ...sx }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="30%" height={32} />
            </Box>
          </Box>
          
          {/* Table Header */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr 1fr 100px',
            gap: 2,
            mb: 2,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="text" height={24} />
            ))}
          </Box>

          {/* Table Rows */}
          {[...Array(skeletonRows)].map((_, rowIndex) => (
            <Box 
              key={rowIndex}
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr 1fr 100px',
                gap: 2,
                mb: 1.5,
                py: 1
              }}
            >
              {[...Array(5)].map((_, colIndex) => (
                <Skeleton key={colIndex} variant="text" height={20} />
              ))}
            </Box>
          ))}
        </Box>
      );

    case 'circular':
    default:
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: sizes.minHeight,
          gap: 2,
          ...sx
        }}>
          {layout === 'stacked' ? (
            // Stacked layout: spinner on top, message below
            <>
              <Typography variant={sizes.textVariant} color="text.secondary">
                {message}
              </Typography>
              <CircularProgress size={sizes.circularSize} thickness={4} />
            </>
          ) : (
            // Inline layout: spinner and message side by side
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant={sizes.textVariant} color="text.secondary">
                {message}
              </Typography>
              <CircularProgress size={sizes.circularSize} thickness={4}  sx={{ color: "primary" }}/>
            </Box>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.disabled">
              {subtitle}
            </Typography>
          )}
        </Box>
      );
  }
};

export default Loader;